'use client'
import dynamic from 'next/dynamic'
import { WfsPage, tenant2, FdrPage, FfnPage, BillsPage } from 'utils/environmentConfig'
import { Image } from 'components/Image'
import { AchieveLink } from 'components/AchieveLink'
import { mainProductConfig } from 'constants/mainProductConfig'
import { useCallback } from 'react'
import { Typography as OwnerTypography } from 'components/Contentful'
import { Button } from '@achieve/ascend'
import { SignInLink } from 'components/SignInLink'
import useMobileNavState from 'hooks/useMobileNavState'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import MenuIcon from 'components/Svgs/Menu.js'
import Close from 'components/Svgs/Close.js'
import useIsSsr from 'hooks/useIsSsr'
import classNames from 'classnames'
import Navigation from 'components/Navigation'
import HeaderCta from 'components/Header/HeaderCta'
import useLayoutMenuCondensed from '@/hooks/useLayoutMenuCondensed'
import { useClientDashboardLoginOverlayTrigger } from '../../../hooks/useClientDashboardLoginOverlayTrigger/useClientDashboardLoginOverlayTrigger.js'
import { useAnalyticsTrackEvent } from 'hooks/useAnalyticsTrackEvent'

const AchieveLogoRSVG = dynamic(() => import('components/Svgs/AchieveLogoRSVG.js'))
const Website2LogoSVG = dynamic(() => import('components/Svgs/Website2LogoSVG.js'))
const Website2LogoWhiteSVG = dynamic(() => import('components/Svgs/Website2LogoWhiteSVG.js'))
const Website3LogoSVG = dynamic(() => import('components/Svgs/Website3LogoSVG.js'))
const WebsiteBillsLogoSVG = dynamic(() => import('components/Svgs/WebsiteBillsLogoSVG.js'))
const WebsiteBillsLogoWhiteSVG = dynamic(() =>
  import('components/Svgs/WebsiteBillsLogoWhiteSVG.js')
)
const CurrentSvg = ({ siteEnv, ...props }) => {
  switch (siteEnv) {
    case WfsPage:
      return (
        <Image
          src="/wfs-logo-dark.png"
          title="WFS Logo"
          alt="WFS Logo"
          width={246}
          height={87}
          {...props}
        />
      )
    case tenant2:
      return <Website2LogoSVG title="website-tenant-2" alt="website-tenant-2" {...props} />
    case FdrPage:
      return <Website2LogoSVG title="FDR Logo" alt="FDR Logo" {...props} />
    case FfnPage:
      return <Website3LogoSVG title="FFN Logo" alt="FFN Logo" {...props} />
    case BillsPage:
      return <WebsiteBillsLogoSVG title="Bills Logo" alt="Bills Logo" {...props} />
    default:
      return <AchieveLogoRSVG title="Achieve Logo" alt="Achieve Logo" {...props} />
  }
}

export default function HeaderMobileLeft({
  siteEnv = 'achieve',
  logoEvent,
  signInLinkUrl,
  signInLinkText,
  linkUrl,
  linkText,
  disabledRoutes,
  linkEvent,
  mainNavigation,
  mainNavigationMobile,
  disabledElements,
  isHomePath,
  phoneData,
}) {
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const { fireAndForget } = useTriggerEvents()
  const [menuCondensed] = useLayoutMenuCondensed()
  const isSsr = useIsSsr()
  const { openOverlayInsteadOfNavigating } = useClientDashboardLoginOverlayTrigger()
  const { analyticsTrackEvent } = useAnalyticsTrackEvent()

  const phoneHref = phoneData?.phoneNumber ? `tel:${phoneData.phoneNumber}` : undefined

  const handleCloseClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
    setMobileNavOpen(false)
  }

  const handleOpenClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    setMobileNavOpen(true)
  }

  const handleButtonClick = useCallback(
    //todo changed as CTA button, but destination url is https://fdr-dev.programdashboard.com/login
    (href, name, isLeadCta) => {
      analyticsTrackEvent({
        nav_link_section: 'Header',
        click_type: 'Button Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_header',
        event_type: isLeadCta ? 'conversion' : 'track',
      })
    },
    [analyticsTrackEvent]
  )
  const CallUsButton = () => (
    <AchieveLink
      href={phoneHref}
      track={{
        list_name: 'CALL STICKY HEADER',
        click_id: phoneData?.phoneNumber,
        click_text: `Call us`,
        click_url: phoneData?.phoneNumber,
        click_type: 'Call us Click',
        event_action: 'button_click',
        nav_link_section: 'Sticky Header',
        track_event: 'call-sticky- hidden lg:block',
      }}
      data-testid="call-sticky hidden lg:block"
    >
      <Button
        component="span"
        color="secondary"
        variant="contained"
        className={classNames('flex flex-row gap-2', { 'whitespace-nowrap': siteEnv === FdrPage })}
        size="small"
      >
        <OwnerTypography variant="displayXxs" fontWeight="bold" content="Call Us" />
      </Button>
    </AchieveLink>
  )

  const HeaderCtaButton = () => (
    <HeaderCta
      siteEnv={siteEnv}
      linkUrl={linkUrl}
      linkText={linkText}
      size="small"
      data-testid="header-get-started-button"
    />
  )
  const DashboardCtaButton = () => (
    <SignInLink
      siteEnv={siteEnv}
      disabledRoutes={disabledRoutes}
      data-testid="mobile-nav-sign-in-link"
      signInLinkText={signInLinkText}
      signInLinkUrl={signInLinkUrl}
      outlined={siteEnv === FdrPage}
      onClick={(e) => {
        openOverlayInsteadOfNavigating(e)
        handleButtonClick(signInLinkUrl, 'Sign In', false)
      }}
    />
  )

  const getSubNav = () => {
    if (siteEnv === FdrPage) {
      return (
        <div className="flex flex-col gap-4 px-2 py-6 text-center justify-center items-center">
          <DashboardCtaButton />
          <HeaderCtaButton />
          <div className="flex flex-col gap-4 items-center">
            <div className="flex flex-col gap-2">
              <OwnerTypography
                variant="bodySm"
                fontWeight="regular"
                content="Talk to a certified debt consultant"
              />
              <OwnerTypography
                variant="bodyMd"
                fontWeight="regular"
                content={phoneData?.phoneNumber}
              />
            </div>
            <CallUsButton />
          </div>
        </div>
      )
    }
  }

  return (
    <div
      className={classNames('p-2', {
        // top-0 keeps it in view while the body is offset by the mobile nav scroll lock
        'fixed top-0 bg-white z-100 w-full': siteEnv === FdrPage && !isHomePath,
      })}
    >
      <header
        className={classNames(
          'flex flex-row  items-center gap-4 transition-transform duration-300 ease-in-out transform',
          {
            'translate-x-68': mobileNavOpen,
          },
          {
            'justify-between': siteEnv === FdrPage,
          }
        )}
      >
        <div className="flex flex-col justify-center items-center">
          {!mobileNavOpen && (
            <button
              onClick={(e) => {
                handleOpenClick(e, {
                  nav_link_section: 'Toggle menu',
                  click_type: 'Button Click',
                  click_id: 'open button',
                })
              }}
              className="border-none bg-transparent"
              data-testid="mobile-nav-open-button"
              aria-label="Main navigation menu button"
            >
              <MenuIcon />
            </button>
          )}
          {mobileNavOpen && (
            <button
              onClick={(e) => {
                handleCloseClick(e, {
                  nav_link_section: 'Toggle menu',
                  click_type: 'Button Click',
                  click_id: 'close button',
                })
              }}
              className="border-none bg-transparent"
              role="button"
              data-testid="mobile-nav-close-button"
              aria-label="Main navigation menu close button"
            >
              <Close title="Close button" width={18} height={18} />
            </button>
          )}
        </div>
        <div
          className={classNames({
            'w-[90px]': siteEnv === FdrPage,
            'w-full flex justify-center': siteEnv === BillsPage,
          })}
        >
          <AchieveLink
            track={{
              ...{
                list_name: 'Header Logo',
                click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
                click_text: `${
                  mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                } | Header logo`,
                nav_link_section: 'Header logo',
                track_event: 'global_header',
                event_action: 'link_click',
                event_type: 'link_click',
              },
              ...logoEvent,
            }}
            href="/"
            withNextLink
            className={''}
            data-testid={`${String(
              mainProductConfig[siteEnv || 'achieve']?.['MainName']
            )?.toLowerCase()}-header-logo`}
          >
            <CurrentSvg siteEnv={siteEnv} />
          </AchieveLink>
        </div>
        {siteEnv === FdrPage && (
          <div className={classNames({ 'invisible ': menuCondensed })}>
            <CallUsButton />
          </div>
        )}
        {siteEnv === FdrPage && (
          <div className={classNames({ 'invisible ': menuCondensed })}>
            <HeaderCtaButton />
          </div>
        )}
      </header>
      <nav
        className={classNames(
          'min-w-68 w-68 bg-white h-screen absolute top-0 left-0 overflow-hidden z-10 transition-transform duration-300 ease-in-out transform',
          { '-translate-x-68': !mobileNavOpen, 'translate-x-0': mobileNavOpen }
        )}
      >
        {siteEnv === FdrPage && (
          <div className="flex flex-row bg-[#305a78] p-3">
            <Website2LogoWhiteSVG width={80} />
          </div>
        )}
        {siteEnv === BillsPage && (
          <div className="flex flex-row bg-[#305a78] p-3">
            <WebsiteBillsLogoWhiteSVG width={110} />
          </div>
        )}
        <Navigation
          event={linkEvent}
          mobileNavOpen={true}
          items={siteEnv === FdrPage ? mainNavigationMobile : mainNavigation}
          disabledRoutes={disabledRoutes}
          disabledElements={disabledElements}
          mobileSubnav={getSubNav()}
          className={classNames('py-0 min-w-68 w-full overflow-hidden', {
            'pt-0! md:pt-[54px]': siteEnv === FdrPage,
          })}
          titleVariant={'bodySm'}
          titleMobileVariant={'bodySm'}
          subNavClassName={'bg-gray-100 shadow-inner'}
          titleClassName={'font-light!'}
          subNavTitleClassName={'font-light!'}
          onItemClick={(e, { url }) => {
            if (siteEnv !== FdrPage || !url || !signInLinkUrl) return
            try {
              const itemPath = new URL(url, 'http://_').pathname
              const signInPath = new URL(signInLinkUrl, 'http://_').pathname
              if (itemPath === signInPath) {
                openOverlayInsteadOfNavigating(e)
                handleButtonClick(signInLinkUrl, 'Sign In', false)
              }
            } catch {
              // ignore malformed URLs
              console.error('Malformed URL in navigation item or sign-in link', {
                url,
                signInLinkUrl,
              })
            }
          }}
        />
      </nav>
    </div>
  )
}
